import {
    Descendant,
    Editor,
    Element as SlateElement,
    Node as SlateNode,
    Transforms,
    Text as SlateText
} from "slate";
import { ReactEditor } from "slate-react";

import {
    RichTextBoxEditor,
    RichTextBoxBaseElement,
    RichTextBoxBulletedListElement,
    RichTextBoxElement,
    RichTextBoxElementListType,
    RichTextBoxElementType,
    RichTextBoxFormatValue,
    RichTextBoxListItemElement,
    RichTextBoxNumberedListElement,
    RichTextBoxText
} from "./richTextEditorTypes";

export namespace RichTextBoxUtils {
    /**
     * Runs through our Descendant Schema and converts the whole document to a plaintext string
     * @param descendant 
     * @returns 
     */
    export const serializeRichTextToPlainText = (descendant: Descendant[]) => {
        const plainText = descendant.map(n => {
            return SlateNode.string(n);
        }).map(str => {
            if (str)
                return `<p><span>${str.trim()}</span></p>`
            return '<p><span><br /></span></p>'
        }).join('') ?? '';
        return `${plainText ?? ''}`;
    }

    /**
     * Determine if the descenant tree is convertible to plain text.
     * If any format, alignment, color etc exists anywhere in the tree this will return false
     * if nothing has formatting and it's just div's with text it will return true.
     * @param node 
     * @returns 
     */
    export const canConvertDescendantToPlainText = (nodes: Descendant[]): boolean => {
        const plainText = serializeRichTextToPlainText(nodes);
        if (!plainText || plainText === '') { //this is so if the user pressed toolbar buttons we don't save empty html.
            return true;
        }
        const topLevelResults = nodes.map((node) => {
            if (SlateElement.isElement(node)) {
                //nothing other than a div can be considered to be convertible to plain text
                if (node.type !== 'paragraph')
                    return false;
                if (node.attrs || node.indentationLevel)
                    return false;
            }
            if (SlateText.isText(node)) {
                if (node.color || node.format) {
                    return false;
                }
            }
            const children = Array.from((node as unknown as any).children ?? []).map(childNode => canConvertDescendantToPlainText([childNode as Descendant])).flat();
            return children.filter(a => !a).length == 0; //<- no child returned false            
        });
        return topLevelResults.filter(a => !a).length == 0; //<- no top level child returned false;
    }


    export const isListElementType = (type: RichTextBoxElementType): type is RichTextBoxElementListType => {
        if (type === "numbered-list" || type === "bulleted-list")
            return true;
        return false;
    }

    export const isListElement = (node: SlateNode | undefined): node is RichTextBoxBulletedListElement | RichTextBoxNumberedListElement => {
        if (SlateElement.isElement(node))
            return isListElementType(node.type);
        return false;
    }

    /**
    * Returns true if the node is a Numbered List Item or Bulleted List Item
    * @param node 
    * @returns 
    */
    export const isListItemElement = (node: Descendant | undefined): node is RichTextBoxListItemElement => {
        if (SlateElement.isElement(node))
            if (node.type === "list-item") {
                return true;
            }
        return false;
    }

    /**
     * Get's the currently selected focused block element from Slate's editor.
     * @param editor 
     * @returns 
     */
    export const getSelectedFocusedElement = (editor: RichTextBoxEditor): RichTextBoxElement | undefined => {
        const { selection } = editor;
        let selectedNode: RichTextBoxElement | RichTextBoxText | undefined = undefined;
        if (selection) {
            if (selection !== null && selection.anchor !== null && selection.anchor.path.length) {
                selectedNode = editor.children[selection.anchor.path[0]];
            }
        }
        if (SlateElement.isElement(selectedNode)) {
            return selectedNode;
        }
        return undefined;
    }

    /**
     * Will break out of a currently focused list if the last item is empty.
     * @param editor 
     * @returns 
     */
    export const tryRemoveEmptyListItemAndBreak = (editor: RichTextBoxEditor) => {
        const { selection } = editor;
        if (selection && selection.focus.path.length) {
            const selectedElement = SlateNode.descendant(editor, [selection.focus.path[0]]);
            if (SlateElement.isElement(selectedElement)) {
                if (isListElement(selectedElement)) {
                    const lastListItem = _.last(selectedElement.children ?? []);
                    if (lastListItem && isListItemElement(lastListItem)) {
                        const onlyItem = (selectedElement.children ?? []).length === 1;
                        var textChildren = lastListItem.children?.filter((v): v is RichTextBoxText => SlateText.isText(v));
                        if (textChildren && textChildren.length > 0 && textChildren[0].text === '') {
                            const lastListItemPath = ReactEditor.findPath(editor, lastListItem);

                            if (onlyItem) {
                                const listPath = ReactEditor.findPath(editor, selectedElement);
                                Transforms.delete(editor, { at: listPath });
                                Transforms.insertNodes(editor, { type: 'paragraph', children: [{ text: '' }] }, { at: listPath });
                                Transforms.select(editor, listPath);
                            } else {
                                Transforms.splitNodes(editor, { at: lastListItemPath });
                                Transforms.unwrapNodes(editor);
                                Transforms.setNodes(editor, { type: 'paragraph' });
                            }
                            return true;
                        }
                    }
                }
            }
        }
        return false;
    }


    //#region Format/Align/Block
    export const getActiveColor = (editor: RichTextBoxEditor) => {
        const currentMarks = Editor.marks(editor) ?? {};
        return currentMarks.color;
    }
    export const setActiveColor = (editor: ReactEditor, color: string | undefined) => {
        editor.addMark('color', color);
        ReactEditor.focus(editor);
    }
    export const isFormatActive = (editor: RichTextBoxEditor, format: RichTextBoxFormatValue) => {
        const currentMarks = Editor.marks(editor) ?? { format: RichTextBoxFormatValue.none } as RichTextBoxText;
        return ((currentMarks.format ?? RichTextBoxFormatValue.none) & format) === format;
    }
    export const toggleFormat = (editor: RichTextBoxEditor, format: RichTextBoxFormatValue) => {
        const currentMarks = Editor.marks(editor) ?? { format: RichTextBoxFormatValue.none } as RichTextBoxText;
        let currentFormat = currentMarks.format ?? RichTextBoxFormatValue.none;

        if ((currentFormat & format) === format) {
            currentFormat = currentFormat - format;
            editor.removeMark('format');
            editor.addMark('format', currentFormat);
        } else {
            currentFormat = currentFormat | format;
            editor.removeMark('format');
            editor.addMark('format', currentFormat);
        }
        ReactEditor.focus(editor);
    };

    export const isBlockActive = (editor: RichTextBoxEditor, elementType: RichTextBoxElementType) => {
        const { selection } = editor
        if (selection) {
            const [match] = Array.from(
                Editor.nodes(editor, {
                    at: Editor.unhangRange(editor, selection),
                    match: (n: SlateNode) =>
                        !Editor.isEditor(n) &&
                        SlateElement.isElement(n) &&
                        n.type === elementType
                }));
            return match && match.length > 0;
        }
        return false;
    }

    export const toggleBlock = (editor: RichTextBoxEditor, elementType: RichTextBoxElementType, active: boolean) => {
        const isListButton = elementType === "numbered-list" || elementType === "bulleted-list";

        const selectedBlockElement = getSelectedFocusedElement(editor);
        if (isListButton || isListElement(selectedBlockElement)) {
            Transforms.unwrapNodes(editor, {
                match: (n) => {
                    return !Editor.isEditor(n) &&
                        SlateElement.isElement(n) && isListElement(n)
                },
                split: true,
            })
        }

        let newProperties: Partial<RichTextBoxElement> = {
            type: active ? 'paragraph' : isListButton ? 'list-item' : elementType
        }
        if (newProperties.type === 'list-item')
            newProperties.indentationLevel = undefined;

        Transforms.setNodes(editor, newProperties);
        if (isListButton) {
            //only rewrap the list item if it's being made active.
            if (!active) {
                const block: RichTextBoxBaseElement = { type: (elementType as RichTextBoxElementType) }
                Transforms.wrapNodes(editor, block as RichTextBoxElement)
            }
        }
        ReactEditor.focus(editor);
    }
    //#endregion
}
